import React from 'react';
import { Modal, Button, Space, Typography, Tag, Spin, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { smartDownload } from '../../utils/downloadToWorkspace';

/**
 * DAS-1658: Equity Check Modal Component
 * 
 * Displays comprehensive equity check results with:
 * - Explanatory header about no-events vs with-events scenarios
 * - Engine validation results (primary check)
 * - Strategy performance comparison
 * - Raw diagnostic data
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.eqCheck - Equity check data from backend
 * @param {Object} props.runInfo - Run information (run_id, config, etc.)
 * @param {Object} props.config - Current configuration
 * @param {Function} props.onCreateTicket - Handler for creating Jira ticket
 * @param {Function} props.onRefresh - DAS-1748: Handler to force refresh equity check
 */
const EquityCheckModal = ({ open, onClose, eqCheck, runInfo, config, onCreateTicket, callAction, onRefresh }) => {
  
  const handleDownloadJSON = async () => {
    await smartDownload(
      callAction,
      eqCheck,
      `equity_check_run_${runInfo?.run_id}`,
      { type: 'json', addTimestamp: true, showMessage: message }
    );
  };

  const handleCreateTicket = () => {
    const context = {
      run_id: runInfo?.run_id,
      config: config,
      check: eqCheck,
    };
    const desc = `Equity Check Verification for Run ${runInfo?.run_id}\n\n` +
                 `Config:\n${JSON.stringify(context.config, null, 2)}\n\n` +
                 `Check Data:\n${JSON.stringify(context.check, null, 2)}`;
    
    onCreateTicket(runInfo?.run_id, desc, "Equity Check Issue");
  };

  return (
    <Modal
      title="Equity Curve Check"
      open={open}
      onCancel={onClose}
      footer={
        <Space>
          {/* DAS-1748: Refresh button to force re-fetch from backend */}
          {onRefresh && (
            <Button icon={<ReloadOutlined />} onClick={() => onRefresh()}>
              Refresh
            </Button>
          )}
          <Button onClick={handleCreateTicket}>
            Create Ticket
          </Button>
          <Button onClick={handleDownloadJSON}>
            Download JSON
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      width={800}
    >
      {eqCheck ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            {/* DAS-1658: Completely redesigned Equity Check UI */}
            
            {/* EXPLANATORY HEADER */}
            <div style={{ background: '#e6f7ff', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #91d5ff' }}>
              <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>🔍 How This Check Works</Typography.Title>
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>📊 NO-EVENTS SCENARIO (Benchmark):</strong>
                  <div style={{ paddingLeft: 16, color: '#666' }}>
                    • Start portfolio held without trading<br/>
                    • Used to measure strategy performance<br/>
                    • Positions show start quantities (no changes)
                  </div>
                </div>
                <div>
                  <strong>⚙️ WITH-EVENTS SCENARIO (Engine):</strong>
                  <div style={{ paddingLeft: 16, color: '#666' }}>
                    • Actual trades executed<br/>
                    • Used to validate engine accuracy<br/>
                    • Positions show final quantities
                  </div>
                </div>
                <div style={{ marginTop: 8, fontStyle: 'italic', color: '#1890ff' }}>
                  The validation compares these to ensure the engine correctly applied all trades.
                </div>
              </div>
            </div>

            {/* SECTION 1: ENGINE VALIDATION RESULTS */}
            <div style={{ marginBottom: 20 }}>
              <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12, color: '#1890ff' }}>
                🔍 ENGINE VALIDATION RESULTS (Reliability Check)
              </Typography.Title>
              
              {/* PRIMARY VALIDATION */}
              <div style={{ background: Math.abs(Number(eqCheck.event_attribution?.hierarchical_check?.difference_3 || 0)) < 0.01 ? '#f6ffed' : '#fff2e8', padding: 16, borderRadius: 8, marginBottom: 12, border: `2px solid ${Math.abs(Number(eqCheck.event_attribution?.hierarchical_check?.difference_3 || 0)) < 0.01 ? '#52c41a' : '#faad14'}` }}>
                <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>
                  {Math.abs(Number(eqCheck.event_attribution?.hierarchical_check?.difference_3 || 0)) < 0.01 ? '✅' : '⚠️'} PRIMARY VALIDATION
                </div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: Math.abs(Number(eqCheck.event_attribution?.hierarchical_check?.difference_3 || 0)) < 0.01 ? '#52c41a' : '#faad14', marginBottom: 4 }}>
                  {Math.abs(Number(eqCheck.event_attribution?.hierarchical_check?.difference_3 || 0)) < 0.01 ? 'PASS' : 'REVIEW NEEDED'}
                </div>
                <div style={{ fontSize: 14 }}>
                  Final Equity Discrepancy: <strong>€{Number(eqCheck.event_attribution?.hierarchical_check?.difference_3 || 0).toFixed(6)}</strong>
                  <span style={{ color: '#666', marginLeft: 8 }}>
                    ({Number(eqCheck.event_attribution?.hierarchical_check?.difference_3_pct || 0).toFixed(8)}%)
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#666', marginTop: 8, background: '#fafafa', padding: 8, borderRadius: 4 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>💡 How calculated:</strong>
                  </div>
                  <div style={{ paddingLeft: 12, fontSize: 10 }}>
                    Expected equity (from holdings + cash) = €{Number(eqCheck.event_attribution?.hierarchical_check?.holdings_reconciliation?.expected_equity_from_holdings || 0).toFixed(2)}
                    <br />
                    Engine's equity (what it calculated) = €{Number(eqCheck.event_attribution?.hierarchical_check?.backtested_equity || 0).toFixed(2)}
                    <br />
                    Difference = €{Number(eqCheck.event_attribution?.hierarchical_check?.difference_3 || 0).toFixed(6)}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <strong>What it means:</strong> The engine's math is accurate within rounding error. 
                    Anything under €1 (0.001%) means the engine correctly tracked all trades, dividends, and fees.
                  </div>
                </div>
              </div>

              {/* HOLDINGS RECONCILIATION */}
              <div style={{ background: '#fafafa', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>📁 Holdings Reconciliation</div>
                
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>Cash Verification:</div>
                  <div style={{ paddingLeft: 12, fontSize: 12 }}>
                    <div>Expected: €{Number(eqCheck.event_attribution?.hierarchical_check?.holdings_reconciliation?.expected_cash || 0).toFixed(2)}</div>
                    <div>Engine: €{Number(eqCheck.event_attribution?.hierarchical_check?.holdings_reconciliation?.engine_cash || 0).toFixed(2)}</div>
                    <div style={{ color: Math.abs(Number(eqCheck.event_attribution?.hierarchical_check?.holdings_reconciliation?.cash_discrepancy || 0)) > 0.01 ? '#cf1322' : '#52c41a', fontWeight: 'bold' }}>
                      {Math.abs(Number(eqCheck.event_attribution?.hierarchical_check?.holdings_reconciliation?.cash_discrepancy || 0)) < 0.01 ? '✅' : '❌'} Discrepancy: €{Number(eqCheck.event_attribution?.hierarchical_check?.holdings_reconciliation?.cash_discrepancy || 0).toFixed(6)}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>Positions Verification:</div>
                  <div style={{ paddingLeft: 12, fontSize: 12 }}>
                    {eqCheck.event_attribution?.hierarchical_check?.holdings_reconciliation?.positions_discrepancies?.length > 0 ? (
                      <div style={{ color: '#cf1322' }}>
                        ❌ {eqCheck.event_attribution.hierarchical_check.holdings_reconciliation.positions_discrepancies.length} mismatches found!
                        {eqCheck.event_attribution.hierarchical_check.holdings_reconciliation.positions_discrepancies.map((d, i) => (
                          <div key={i} style={{ fontSize: 11, paddingLeft: 8, marginTop: 2 }}>
                            {d.ticker}: Expected {d.expected} vs Engine {d.engine} (diff: {d.diff})
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: '#52c41a', fontWeight: 'bold' }}>✅ All holdings match exactly</div>
                    )}
                  </div>
                </div>

                {/* Show Start vs Final Holdings */}
                {eqCheck.positions && eqCheck.positions.length > 0 && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #d9d9d9' }}>
                    <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>📊 Portfolio Holdings:</div>
                    <div style={{ paddingLeft: 12, fontSize: 11 }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Start (No-Events):</strong> {eqCheck.positions.map(p => `${p.ticker}=${p.qty_start}`).join(', ')}
                      </div>
                      <div style={{ color: '#666' }}>
                        <em>Final holdings calculated from: Start + Net Trades (see Events table)</em>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SECTION 2: STRATEGY PERFORMANCE */}
            <div style={{ marginBottom: 20 }}>
              <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12, color: '#52c41a' }}>
                📈 STRATEGY PERFORMANCE (vs Buy & Hold)
              </Typography.Title>
              
              <div style={{ background: '#fafafa', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Buy & Hold Equity (no trades):</span>
                  <span style={{ fontWeight: 'bold' }}>€{Number(eqCheck.totals?.expected_end_equity_no_events || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Strategy Equity (with trades):</span>
                  <span style={{ fontWeight: 'bold' }}>€{Number(eqCheck.totals?.engine_end_equity || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #d9d9d9' }}>
                  <span style={{ fontWeight: 'bold' }}>Performance Difference:</span>
                  <span style={{ fontWeight: 'bold', color: (Number(eqCheck.totals?.engine_end_equity || 0) - Number(eqCheck.totals?.expected_end_equity_no_events || 0)) >= 0 ? '#52c41a' : '#cf1322' }}>
                    {(Number(eqCheck.totals?.engine_end_equity || 0) - Number(eqCheck.totals?.expected_end_equity_no_events || 0)) >= 0 ? '+' : ''}€{(Number(eqCheck.totals?.engine_end_equity || 0) - Number(eqCheck.totals?.expected_end_equity_no_events || 0)).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Cash Flow Summary */}
              <div style={{ background: '#fafafa', padding: 12, borderRadius: 8 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>💰 Cash Flow Summary</div>
                <div style={{ fontSize: 12, paddingLeft: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>Dividends Received:</span>
                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>+€{Number(eqCheck.event_attribution?.dividends_eur || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>Trade Cash Received:</span>
                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>+€{Math.abs(Number(eqCheck.event_attribution?.trade_cash_impact_eur || 0)).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>Fees Paid:</span>
                    <span style={{ color: '#cf1322', fontWeight: 'bold' }}>-€{Math.abs(Number(eqCheck.event_attribution?.fees_eur || 0)).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>Slippage Costs:</span>
                    <span style={{ color: '#cf1322', fontWeight: 'bold' }}>-€{Math.abs(Number(eqCheck.event_attribution?.slippage_eur || 0)).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>Borrowing Costs:</span>
                    <span style={{ color: '#cf1322', fontWeight: 'bold' }}>-€{Math.abs(Number(eqCheck.event_attribution?.borrowing_costs_eur || 0)).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #d9d9d9' }}>
                    <span style={{ fontWeight: 'bold' }}>Net Cash Impact:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      €{(Number(eqCheck.event_attribution?.dividends_eur || 0) + Number(eqCheck.event_attribution?.trade_cash_impact_eur || 0) - Math.abs(Number(eqCheck.event_attribution?.fees_eur || 0)) - Math.abs(Number(eqCheck.event_attribution?.slippage_eur || 0)) - Math.abs(Number(eqCheck.event_attribution?.borrowing_costs_eur || 0))).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#666', marginTop: 8, background: '#fafafa', padding: 8, borderRadius: 4 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>💡 How calculated:</strong>
                  </div>
                  <div style={{ paddingLeft: 12, fontSize: 10 }}>
                    Dividends received: +€{Number(eqCheck.event_attribution?.dividends_eur || 0).toFixed(2)}
                    <br />
                    Trade cash (bought more than sold): +€{Number(eqCheck.event_attribution?.trade_cash_impact_eur || 0).toFixed(2)}
                    <br />
                    Fees paid: -€{Math.abs(Number(eqCheck.event_attribution?.fees_eur || 0)).toFixed(2)}
                    <br />
                    Slippage costs: -€{Math.abs(Number(eqCheck.event_attribution?.slippage_eur || 0)).toFixed(2)}
                    <br />
                    Total: €{(Number(eqCheck.event_attribution?.dividends_eur || 0) + Number(eqCheck.event_attribution?.trade_cash_impact_eur || 0) - Math.abs(Number(eqCheck.event_attribution?.fees_eur || 0)) - Math.abs(Number(eqCheck.event_attribution?.slippage_eur || 0)) - Math.abs(Number(eqCheck.event_attribution?.borrowing_costs_eur || 0))).toFixed(2)}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <strong>What it means:</strong> This is the total change in your cash position from all events. 
                    Includes dividends, net trades, fees, and slippage (bid-ask spread + market impact).
                  </div>
                </div>
              </div>
            </div>

            {/* DAS-1773: MARKET CAP DISTRIBUTION ANALYSIS */}
            {eqCheck.event_attribution?.market_cap_analysis && (
              <div style={{ marginBottom: 20 }}>
                <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12, color: '#722ed1' }}>
                  📊 MARKET CAP DISTRIBUTION (Slippage Analysis)
                </Typography.Title>
                
                <div style={{ background: '#f9f0ff', padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid #d3adf7' }}>
                  {/* Summary Stats */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 12 }}>
                    <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                        {eqCheck.event_attribution.market_cap_analysis.summary?.total_trades || 0}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>Total Trades</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                        €{Number(eqCheck.event_attribution.market_cap_analysis.summary?.total_volume_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>Total Volume</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#cf1322' }}>
                        €{Number(eqCheck.event_attribution.market_cap_analysis.summary?.total_slippage_eur || 0).toFixed(2)}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>Total Slippage</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#faad14' }}>
                        {Number(eqCheck.event_attribution.market_cap_analysis.summary?.weighted_avg_slippage_pct || 0).toFixed(3)}%
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>Avg Slippage</div>
                    </div>
                  </div>

                  {/* By Tier Table */}
                  <div style={{ background: 'white', borderRadius: 6, padding: 8 }}>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #d3adf7' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px' }}>Tier</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Trades</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Volume</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>% of Total</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Slippage</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Slip %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {['large_cap', 'mid_cap', 'small_cap', 'unknown'].map(tier => {
                          const data = eqCheck.event_attribution.market_cap_analysis.by_tier?.[tier];
                          if (!data || data.trade_count === 0) return null;
                          const tierLabels = {
                            large_cap: { icon: '🔵', label: 'Large Cap', color: '#1890ff' },
                            mid_cap: { icon: '🟡', label: 'Mid Cap', color: '#faad14' },
                            small_cap: { icon: '🔴', label: 'Small Cap', color: '#cf1322' },
                            unknown: { icon: '⚪', label: 'Unknown', color: '#666' }
                          };
                          const t = tierLabels[tier];
                          return (
                            <tr key={tier} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '6px 8px' }}>{t.icon} {t.label}</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px' }}>{data.trade_count}</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px' }}>€{Number(data.volume_eur).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', color: t.color, fontWeight: 'bold' }}>{data.volume_pct}%</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', color: '#cf1322' }}>€{Number(data.slippage_eur).toFixed(2)}</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px' }}>{data.implied_slippage_pct}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Thresholds */}
                  <div style={{ fontSize: 10, color: '#666', marginTop: 8 }}>
                    <strong>Thresholds:</strong> Large Cap ≥ €{(eqCheck.event_attribution.market_cap_analysis.thresholds?.large_cap_min_eur / 1e9).toFixed(0)}B | 
                    Mid Cap ≥ €{(eqCheck.event_attribution.market_cap_analysis.thresholds?.mid_cap_min_eur / 1e9).toFixed(0)}B
                  </div>
                </div>
              </div>
            )}

            {/* DAS-1665: Removed confusing "RAW DATA" section with residual - it was misleading users */}
          </div>
        </Space>
      ) : (
        <Spin />
      )}
    </Modal>
  );
};

export default EquityCheckModal;

